import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Cluster,
  ClusterConfiguration,
  ClusterService,
  ClusterUpdateInput,
  TanMaxCreateInput,
} from 'src/app/core/cluster.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';

const CLUSTER_INSTALLMENT_STEPS = [4, 6, 8, 10, 12];

@Component({
  selector: 'app-cluster-version-dialog',
  templateUrl: './cluster-version-dialog.component.html',
})
export class ClusterVersionDialogComponent {
  cluster?: Cluster;
  cluster_code: string;
  cluster_id: string;
  name: string;
  form: FormGroup;
  is_submit_running: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { cluster?: Cluster; cluster_id: string; cluster_code: string; name?: string },
    private dialog_ref: MatDialogRef<ClusterVersionDialogComponent>,
    private form_builder: FormBuilder,
    private cluster_service: ClusterService,
    private error_service: ErrorService,
  ) {
    this.cluster = data.cluster;
    this.cluster_id = data.cluster_id;
    this.cluster_code = data.cluster_code;
    this.name = data.name || '';
    this.form = this.form_builder.group({
      valid_from: ['', Validators.required],
      valid_to: ['', Validators.required],
      configurations: this.form_builder.array([]),
    });
  }

  ngOnInit(): void {
    if (this.cluster) {
      this.form.patchValue({ valid_from: new Date(this.cluster.valid_from), valid_to: new Date(this.cluster.valid_to) });
      this.cluster.configurations.forEach(configuration => this.configurations.push(this.createConfigurationGroup(configuration)));
      return;
    }

    const today = new Date();
    this.form.patchValue({ valid_from: today, valid_to: today });
    CLUSTER_INSTALLMENT_STEPS.forEach(steps_number =>
      this.configurations.push(
        this.createConfigurationGroup({
          steps_number,
          min_amount: 0,
          max_amount: 0,
          goal_tan: 0,
          min_tan: 0,
          max_tan: 0,
        }),
      ),
    );
  }

  get configurations(): FormArray {
    return this.form.get('configurations') as FormArray;
  }

  onCloseClick(): void {
    this.dialog_ref.close();
  }

  onSubmitClick(): void {
    if (this.form.invalid || !this.configurations.length || !this.isValidDateRange()) return;

    const value = this.form.value;
    const valid_from = new Date(value.valid_from).getTime();
    const configurations = value.configurations.map((configuration: ClusterConfiguration) => ({
      steps_number: configuration.steps_number,
      value: Number(configuration.max_tan) * 100,
    }));

    this.is_submit_running = true;
    const request = this.cluster?.cluster_id
      ? this.cluster_service.updateCluster(this.cluster.cluster_id, {
          valid_from,
          valid_to: new Date(value.valid_to).getTime(),
          configurations: value.configurations.map((configuration: ClusterConfiguration) => ({
            ...configuration,
            max_tan: Number(configuration.max_tan) * 100,
          })),
        } as ClusterUpdateInput)
      : this.cluster_service.createTanMax({
          valid_from,
          cluster_id: this.cluster_id,
          cluster_code: this.cluster_code,
          configurations,
        } as TanMaxCreateInput);
    request.subscribe({
      next: () => {
        this.dialog_ref.close({ success: true });
        this.is_submit_running = false;
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.dialog_ref.close({ error: processed_error });
        this.is_submit_running = false;
      },
    });
  }

  private createConfigurationGroup(configuration: ClusterConfiguration): FormGroup {
    return this.form_builder.group({
      steps_number: [configuration.steps_number],
      min_amount: [configuration.min_amount],
      max_amount: [configuration.max_amount],
      goal_tan: [configuration.goal_tan],
      min_tan: [configuration.min_tan],
      max_tan: [configuration.max_tan / 100, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  private isValidDateRange(): boolean {
    const valid_from = new Date(this.form.get('valid_from')?.value).getTime();
    const valid_to = new Date(this.form.get('valid_to')?.value).getTime();
    return !Number.isNaN(valid_from) && !Number.isNaN(valid_to) && valid_from <= valid_to;
  }
}